/**
 * Colour-token programme, batch 2 — antd's theme is seeded from the palette.
 *
 * WHY THIS FILE EXISTS. antd paints most of this app's pixels and, until this
 * change, none of them came from `palette.ts`: `AntdI18n` handed antd the
 * `@ant-design/compatible` v4 pair, whose `token` carries three keys
 * (`borderRadius`, `colorPrimary: '#1890ff'`, `wireframe`), and everything else
 * was derived by the compat algorithm from antd's own stock defaults. A runtime
 * census of the login page measured 540 of the 617 hardcoded colour
 * declarations on the page coming out of antd's cssinjs sheets, none of them
 * reachable from our source. This module is the seam that makes them
 * palette-derived. It is a STOPGAP for as long as antd is mounted
 * (`fe-drop-antd-when-migrated`); the shadcn oklch set is authoritative going
 * forward, and antd cannot consume it (see RAW VALUES below).
 *
 * RAW VALUES, NEVER `var(--wh-*)`. antd's cssinjs does colour MATHS on these —
 * `generate()` ramps, `TinyColor().setAlpha/darken/lighten` — so a custom-
 * property string parses as garbage rather than throwing. Same constraint, same
 * reason as `src/antd/Tag/utils.ts`. Values come from `rawTheme()`.
 *
 * SEEDS vs MAP OVERRIDES — the two halves below are not the same mechanism, and
 * mixing them up is how a value silently fails to apply. antd deletes every
 * SEED key from the `token` override before merging (`antd/es/theme/util/
 * alias.js`: `Object.keys(seedToken).forEach(t => delete overrideTokens[t])`),
 * because a seed has already been consumed by the algorithm. The eight seed
 * keys are colorPrimary, colorSuccess, colorWarning, colorError, colorInfo,
 * colorLink, colorTextBase and colorBgBase. Everything else here is a map token
 * and lands literally.
 *
 * WHAT A SEED ACTUALLY PAINTS. In LIGHT the compat algorithm returns the seed
 * unchanged (`generateColorPalettes(x)[6] === generate(x)[5] === x`), so a light
 * seed paints its literal value. In DARK the compat pair swaps in
 * `theme.darkAlgorithm` — easy to miss, because `JSON.stringify(darkTheme)`
 * drops the function — and every seed is re-derived against antd's dark
 * background (`#1890FF` → `#177ddc`, `#40A9FF` → `#3993dc`). Two consequences:
 * a dark seed does NOT paint its own value, and a value taken from the palette's
 * DARK ramp — which `palette.ts` already generated with the same dark
 * generator — would be darkened twice. So the status/brand seeds below are read
 * from the LIGHT accents in both themes and antd does the per-theme adaptation.
 * The exceptions are the accents the palette deliberately lifts for AA on the
 * dark surfaces (`textDangerous`, and `textLink` in the dark half of
 * `colorLink`); their light values are the light accents anyway, so reading
 * them theme-aware costs nothing in light.
 *
 * A SEED IS CHOSEN FOR ITS CONTRAST, NOT ONLY FOR ITS NAME. Because a light
 * seed paints itself and a dark seed is re-derived, the same token can be legible
 * in one theme and fail WCAG AA in the other — so every accent that lands on
 * TEXT is picked per theme against the surface it actually paints on. The
 * ratios are recorded at each call site below; re-measure them rather than
 * assuming, and never "fix" one theme by a swap that silently breaks the other.
 */
import { rawTheme, ResolvedTheme } from './raw'

// Brand and status HUES. Theme-invariant on purpose: antd re-derives a seed per
// theme, and PRO-509 already ruled that the brand blue is not desaturated for
// dark.
const BRAND = rawTheme('light')

/**
 * The colour half of antd's `theme.token`, for the resolved theme.
 *
 * Spread AFTER the compat theme's own `token` so these win, and BEFORE the
 * design-toggle font token, which is orthogonal.
 */
export function antdPaletteTokens(resolved: ResolvedTheme) {
  const c = rawTheme(resolved)

  return {
    // ── SEEDS (consumed by the algorithm; see the header) ─────────────────
    // #1890FF — the app's brand blue, and byte-identical to what the compat
    // pair already seeded. `colorInfo` was the one antd default nobody set, so
    // it stayed on antd's stock #1677ff and leaked out as a third blue through
    // every bare `<a>` (colorLink falls back to colorInfo when unset).
    colorPrimary: BRAND.blue600,
    colorInfo: BRAND.blue600,
    colorSuccess: BRAND.green600,
    // The palette's semantic feedback tokens, read from LIGHT for the
    // double-darkening reason in the header; `textDangerous` is one of the two
    // accents the palette lifts for dark, so it is read theme-aware
    // (LIGHT.textDangerous === LIGHT.fbCritical, so light is unchanged).
    //
    // `gold600`, NOT the `fbWarning` token, and the two are one ramp step
    // apart: `fbWarning` is gold500 (#FFC53D). gold600 (#FAAD14) is the level
    // every other status seed sits at — colorSuccess green600, colorError
    // red500 via textDangerous, colorPrimary/colorInfo blue600 — and it is
    // byte-identical to the warning antd already derived, so nothing visibly
    // regresses while the seed contrast improves (1.58:1 -> 1.90:1 on white).
    // gold500 and `fbWarning` are the same value, so no palette SEMANTICS are
    // lost here; only the ramp step antd is seeded from moved.
    colorWarning: BRAND.gold600,
    colorError: c.textDangerous,
    // Closes `fe-link-token-apply`: the wrong blue on every bare `<a>` was
    // never a source sweep (there are zero `styled.a` sites in `src`) — it was
    // this seed being unset, so links fell back to `colorInfo` and painted
    // antd's stock #1677ff, a blue in none of our files.
    //
    // NOT `textLink` in light, by captain's ruling (2026-08-24). The approved
    // repaint was to land colours on the palette, not to make links less
    // legible, and `textLink` (#40A9FF) measures 2.52:1 on white — a WCAG AA
    // failure for normal text. `blue700` is the palette entry that clears AA:
    // 5.00:1 on white, 4.33:1 on the `bgSystem` footer, up from #1677ff's
    // 4.10:1 / 3.56:1. The `textLink` TOKEN is deliberately left alone; only
    // the seed moved.
    //
    // Dark keeps `textLink` (#5AA2FF → antd derives #508ddc, 5.23:1 on
    // `bgInnerPrimary`) — the palette lifted that value for AA on these
    // surfaces on purpose. Seeding `blue700` here too would be the ruling
    // applied against its own reason: the dark algorithm re-derives it to
    // #0b60bb, 2.88:1, i.e. it would trade the light failure for a dark one.
    colorLink: resolved === 'dark' ? c.textLink : BRAND.blue700,
    // Drives the whole neutral ladder the algorithm derives by alpha/darken:
    // colorTextTertiary, colorTextQuaternary, colorFill*, colorBgSpotlight,
    // colorBgLayout, colorBorder…, which is why those need no entry of their
    // own here any more.
    colorTextBase: c.textPrimary,
    colorBgBase: c.bgPage,

    // ── MAP OVERRIDES (land literally) ───────────────────────────────────
    // The algorithm would give `alpha(colorTextBase, 0.85)`; body text belongs
    // on `textPrimary` itself.
    colorText: c.textPrimary,
    // NO `colorTextSecondary` override — still derived from `colorTextBase`,
    // and that is now a SCOPE decision rather than a contrast one.
    // `@ant-design/compatible`'s `genColorMapToken` derives it at alpha 0.45
    // (marked `// Different from v5` in its `theme/default.js`, not v5's 0.65),
    // so light lands on rgba(38,38,38,0.45) = #9D9D9D = 2.71:1 on white —
    // BELOW the 4.5:1 WCAG AA floor for normal text.
    //
    // Seeding `c.textSecondary` here would IMPROVE that, not degrade it: the
    // palette token was raised to alpha 0.65 = #727272 = 4.81:1 on white
    // (it was 0.5 = #929292 = 3.11:1, which is what made deriving the better
    // choice when this comment was first written — that reasoning is now
    // reversed).
    //
    // It is not done here because this token paints a large share of the app —
    // secondary `Typography`, form extras, table meta — so seeding it is a
    // product-visible change across many pages. Deferred to a separate
    // decision, not a technical objection.
    // Surfaces. In light these resolve to what antd already derived from a
    // white base, except colorBgLayout, which becomes the app's real page
    // background instead of antd's darken(#fff, 4%).
    colorBgLayout: c.bgSystem,
    colorBgContainer: c.bgInnerPrimary,
    // The palette's top elevation plane (L3) — popover, dropdown, modal,
    // tooltip, menu. White in light, one step above the card in dark.
    colorBgElevated: c.bgHeader,
    // antd's two border levels map onto the palette's two: the control border
    // and the subtler section/divider border.
    colorBorder: c.bInputDefault,
    colorBorderSecondary: c.bSection,

    // ── DARK-ONLY ────────────────────────────────────────────────────────
    // `colorBgSpotlight` is the tooltip fill, and it is the one surface light
    // must NOT take from the palette: in light it is a DARK plane, which the
    // algorithm already derives correctly from `colorTextBase`. Branched here
    // rather than in a separate helper so the guarantee cannot be lost by a
    // caller forgetting the ternary.
    ...(resolved === 'dark' ? { colorBgSpotlight: c.bSection } : {}),
  }
}

/**
 * Dark-only component overrides — antd's `Layout`/`Menu` defaults are the v4
 * navy chrome, which only the dark shell replaces. It takes no theme: reading
 * DARK directly is what makes "dark-only" a property of this module rather
 * than of the call site.
 */
export function antdDarkComponents() {
  const c = rawTheme('dark')

  return {
    Layout: {
      headerBg: c.bgHeader,
      siderBg: c.bgSider,
      bodyBg: c.bgSystem,
    },
    Menu: { darkItemBg: c.bgSider, darkPopupBg: c.bgSider },
  }
}
